/**
 * Pure helpers for the WPC work queue (WARP-951, WARP-959). Kept free of React so they
 * can be unit-tested on their own.
 */

import { BUCKET } from '@/pages/RoStatusDashboard/buckets'

/**
 * A WPC executive's WPC Managed / Dealer Managed switch (the nav's type pill), as a filter
 * for the work queue endpoints, the same way the Dealers list applies it. Only across
 * dealers: one dealer's queue is that dealer's, whatever the switch says. Shared by the
 * queue, the nav badge, and the all-dealers dashboard so all three count the same ROs.
 */
export const managedTypeFilter = ({ dealerId, isWpcExecutive, type }) =>
  isWpcExecutive && !dealerId ? { is_dealer_managed: type === 'dealer' ? 'true' : 'false' } : {}

/** Tells the nav badge the queue changed, so it recounts without waiting to navigate. */
export const WORK_QUEUE_CHANGED = 'warp:work-queue-changed'

/**
 * Hours from receipt before a Ready or Closed RO is late. Matches the API's
 * WorkQueueMixin.SLA_HOURS: the contract's 24 hours to get a closed RO submitted.
 */
export const SLA_HOURS = 24

/** How long before the SLA the queue starts warning. */
export const DUE_SOON_HOURS = 4

/**
 * Hours an Outstanding List RO can wait on WPC before the queue flags it. A guideline,
 * not the contract (Michelle, 2026-10-01), so it warns rather than marks late.
 */
export const GUIDELINE_HOURS = 48

/**
 * Days ahead the queue warns that an Outstanding List RO will age out (WARP-965). Matches
 * the API's AGING_OUT_SOON_DAYS. Michelle: "It's more important that we attack it before
 * it ages out."
 */
export const AGING_OUT_SOON_DAYS = 7

/**
 * The statuses an RO can move to from a row. Dealer closed is not one of them: it is a
 * separate flag, since an RO can be dealer closed *and* on the Outstanding List. Values
 * match `REPAIR_ORDER_STATUS` on the backend.
 */
export const DISPOSITION = {
  CLOSED: 'Closed',
  SUBMITTED: 'Closed — Submitted',
  OUTSTANDING: 'On Outstanding List',
  PAID: 'Paid',
  PAID_ADJUSTED: 'Paid with Adjustments',
}

/**
 * Where an RO can go next from a table row, by its status. The API's
 * `ALLOWED_STATUS_MOVES` (WARP-957), so a row never offers a move the API refuses. Write-
 * offs and Internal are not here: they happen on the Outstanding List alone.
 *
 * Closed and submitted moves from the row too (WARP-966, 2026-10-08): Paid, Paid with
 * adjustments, or back to the Outstanding List when the OEM returns it. That reverses
 * the 2026-10-06 call to work submitted ROs from Manage Repair Order only.
 */
export const NEXT_DISPOSITIONS = {
  'Not Started': [DISPOSITION.CLOSED, DISPOSITION.SUBMITTED, DISPOSITION.OUTSTANDING],
  Closed: [DISPOSITION.SUBMITTED, DISPOSITION.OUTSTANDING],
  'Closed — Submitted': [DISPOSITION.PAID, DISPOSITION.PAID_ADJUSTED, DISPOSITION.OUTSTANDING],
}

const DISPOSITION_LABELS = {
  [DISPOSITION.CLOSED]: 'Closed',
  [DISPOSITION.SUBMITTED]: 'Closed and submitted',
  [DISPOSITION.OUTSTANDING]: 'Outstanding List',
  [DISPOSITION.PAID]: 'Paid',
  [DISPOSITION.PAID_ADJUSTED]: 'Paid with adjustments',
}

/**
 * The status choices for an RO with this status, as `[{ value, label }]`. A submitted RO
 * goes *back* to the Outstanding List: the OEM returned it (WARP-966).
 */
export const dispositionsFor = (status) =>
  (NEXT_DISPOSITIONS[status] || []).map((value) => ({
    value,
    label:
      value === DISPOSITION.OUTSTANDING && status === 'Closed — Submitted'
        ? 'Return to Outstanding List'
        : DISPOSITION_LABELS[value],
  }))

/**
 * The queues, one tab each: one status per view, so the page never holds too much at
 * once (2026-10-01). `id` is the tab in the address and `api` the API's queue name.
 * `group` splits WPC's work from what waits on someone else; `kind` picks the row.
 *
 * WPC's work, counted in the header total and the nav badge:
 * - Ready: logged, not yet worked. Moves to any of the three.
 * - Closed: closed in the DMS, not yet submitted. It must be submitted or put on the
 *   Outstanding List within the same 24 hours.
 * - Admin review: Outstanding List ROs waiting on WPC (WPC Review, and drafts not yet
 *   sent to the dealer). Worked on the RO page, so these rows open it instead.
 *
 * - Pending OEM: closed and submitted, waiting on the OEM. Moves to Paid, Paid with
 *   adjustments, or back to the Outstanding List from the row (WARP-966). WPC's work since
 *   WARP-965: the admin follows up on the OEM's credit memo, so it sits after admin
 *   review (Michelle and Mark, 2026-10-08).
 *
 * Waiting on the dealer, shown but not counted (2026-10-06):
 * - Dealer review: Outstanding List ROs that are the dealer's turn. Flags 48 hours with
 *   no response (Mark's tile, 2026-10-01). Opens the RO page.
 *
 * Every row, on every tab, takes the dealer-closed flag and attachments (WARP-966,
 * 2026-10-08): "if the team doesn't do that, they need a spot to go fix it" (Michelle).
 * That includes Closed, which on 2026-09-28 had no flag because Closed already means
 * closed in the DMS.
 *
 * Finished statuses (Paid, Written off, Internal) have no tab: nothing waits on them,
 * and the dashboard and RO search find them.
 */
export const QUEUES = [
  {
    id: 'ready',
    api: 'ready',
    countsKey: 'ready',
    label: 'Ready',
    bucket: BUCKET.READY,
    description: `ROs received but not yet worked. Close or set a status on every RO within ${SLA_HOURS} hours of receipt.`,
    dispositions: dispositionsFor('Not Started'),
    group: 'work',
    kind: 'work',
    empty: 'Nothing waiting. Every received RO has been worked.',
  },
  {
    id: 'closed',
    api: 'closed',
    countsKey: 'closed',
    label: 'Closed',
    bucket: BUCKET.CLOSED,
    description: `ROs closed in the DMS but not yet submitted. Submit or put each on the Outstanding List within ${SLA_HOURS} hours of receipt.`,
    dispositions: dispositionsFor('Closed'),
    group: 'work',
    kind: 'work',
    empty: 'Nothing waiting. Every closed RO has been submitted or put on the list.',
  },
  {
    id: 'admin-review',
    api: 'admin_review',
    countsKey: 'adminReview',
    label: 'OL - Admin Review',
    // Gold, as WPC Review is on the Outstanding List and the dashboard.
    tone: 'gold',
    description: `Outstanding List ROs waiting on WPC: in WPC review, or drafts not yet sent to the dealer. Answer within ${GUIDELINE_HOURS} hours of the last update.`,
    dispositions: [],
    group: 'work',
    kind: 'outstanding',
    waitingOn: 'WPC',
    empty: 'Nothing waiting. The dealers have every Outstanding List RO.',
  },
  {
    id: 'pending',
    api: 'pending',
    countsKey: 'pending',
    label: 'Pending OEM',
    bucket: BUCKET.PENDING,
    description:
      'Closed and submitted ROs waiting on the OEM. Mark each paid, or return it to the Outstanding List, when the OEM answers.',
    dispositions: dispositionsFor('Closed — Submitted'),
    group: 'work',
    kind: 'pending',
    empty: 'Nothing pending with the OEM.',
  },
  {
    id: 'dealer-review',
    api: 'dealer_review',
    countsKey: 'dealerReview',
    label: 'OL - Dealer Review',
    // Blue, as Dealer Review is on the Outstanding List and the dashboard.
    tone: 'blue',
    description: `Outstanding List ROs waiting on the dealer. Follow up on any with no response in ${GUIDELINE_HOURS} hours.`,
    dispositions: [],
    group: 'waiting',
    kind: 'outstanding',
    waitingOn: 'dealer',
    empty: 'Nothing waiting on the dealers.',
  },
]

export const QUEUE_GROUPS = [
  { id: 'work', label: 'To work' },
  { id: 'waiting', label: 'Waiting on the dealer' },
]

export const queueById = (id) => QUEUES.find((queue) => queue.id === id) || QUEUES[0]

export const hoursSince = (iso, now) => (now - new Date(iso).getTime()) / 3600000

export const hoursInQueue = (row, now) => hoursSince(row.receivedAt, now)

export const isLate = (row, now) => hoursInQueue(row, now) > SLA_HOURS

export const isDueSoon = (row, now) => {
  const hours = hoursInQueue(row, now)
  return hours > SLA_HOURS - DUE_SOON_HOURS && hours <= SLA_HOURS
}

/** Hours an Outstanding List RO has waited on either side: since its last update. */
export const hoursWaiting = (row, now) =>
  row.outstandingList?.lastUpdatedOn ? hoursSince(row.outstandingList.lastUpdatedOn, now) : 0

export const isPastGuideline = (row, now) => hoursWaiting(row, now) > GUIDELINE_HOURS

/** Past the OEM's age-out date. `agesOut` is a date, compared as a calendar day. */
export const isAgedOut = (row, today) =>
  Boolean(row.outstandingList?.agesOut) && row.outstandingList.agesOut < today

/** `YYYY-MM-DD` plus some days, without a time zone to shift it. */
const addDays = (day, days) => {
  const date = new Date(`${day}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

/** Ages out today or within the next `AGING_OUT_SOON_DAYS`, and not already aged out. */
export const isAgingOutSoon = (row, today) => {
  const agesOut = row.outstandingList?.agesOut
  return Boolean(agesOut) && agesOut >= today && agesOut <= addDays(today, AGING_OUT_SOON_DAYS)
}

/** The claim dollars on a set of rows. */
export const claimDollars = (rows) =>
  rows.reduce((sum, row) => sum + (Number(row.totalClaimAmount) || 0), 0)

/**
 * The summary cards' filters for a queue, which also filter its table. Ready and Closed
 * run against the 24-hour SLA; the Outstanding List queues against the 48-hour guideline
 * and age-out; Pending OEM, which has no deadline, by how long it has been out.
 */
export const cardFiltersFor = (queue, now, today) => {
  if (queue.kind === 'outstanding') {
    return {
      agingOutSoon: (row) => isAgingOutSoon(row, today),
      waiting: (row) => isPastGuideline(row, now),
      agedOut: (row) => isAgedOut(row, today),
    }
  }
  if (queue.kind === 'pending') {
    return {
      over30: (row) => hoursInQueue(row, now) > 30 * 24,
      over90: (row) => hoursInQueue(row, now) > 90 * 24,
    }
  }
  return {
    late: (row) => isLate(row, now),
    soon: (row) => isDueSoon(row, now),
  }
}

/**
 * A queue's filter cards, under its tile. Each filters the table; the tile above is the
 * unfiltered view, so there is no "in total" card (Mark, 2026-10-08: "Those two buttons
 * are redundant"). `alert` marks a card red (past a hard deadline), `warn` amber (past
 * the guideline or about to age out). `dollars` is the claim money at stake, where the
 * queue's ROs carry claims (WARP-965).
 */
export const cardsFor = (queue, rows, filters) => {
  // A filter this queue does not have matches nothing rather than throwing.
  const matching = (id) => (filters[id] ? rows.filter(filters[id]) : [])
  const card = (id, label, extra = {}) => {
    const matched = matching(id)
    return { id, label, value: matched.length, dollars: claimDollars(matched), ...extra }
  }

  if (queue.kind === 'outstanding') {
    const soon = card('agingOutSoon', `Aging out in the next ${AGING_OUT_SOON_DAYS} days`)
    const waiting = card(
      'waiting',
      // On the dealer's side this is Mark's "no response in 48 hours" (2026-10-01).
      queue.waitingOn === 'dealer'
        ? `No response in ${GUIDELINE_HOURS} hours`
        : `Over ${GUIDELINE_HOURS} hours, not touched`,
    )
    const agedOut = card('agedOut', 'Aged out')
    return [
      { ...soon, warn: soon.value > 0 },
      { ...waiting, warn: waiting.value > 0 },
      { ...agedOut, alert: agedOut.value > 0 },
    ]
  }
  if (queue.kind === 'pending') {
    return [card('over30', 'Out over 30 days'), card('over90', 'Out over 90 days')]
  }
  // Ready ROs carry no claim yet, so these cards show no dollars.
  const late = card('late', `Past ${SLA_HOURS}-hour SLA`, { dollars: null })
  return [
    { ...late, alert: late.value > 0 },
    card('soon', `Due in the next ${DUE_SOON_HOURS} hours`, { dollars: null }),
  ]
}

/** "3 h 12 m", or "2 d 4 h" once it runs past a day. */
export const formatDuration = (hours) => {
  const safe = Math.max(0, hours)
  if (safe >= 48) return `${Math.floor(safe / 24)} d ${Math.floor(safe % 24)} h`
  return `${Math.floor(safe)} h ${Math.floor((safe % 1) * 60)} m`
}

/** An empty edit: nothing picked, nothing staged. */
export const blankEdit = (row) => ({
  disposition: null,
  dealerClosed: Boolean(row.dealerClosed),
  newFiles: [],
  filesToDelete: [],
})

/** Whether a row carries anything worth saving. */
export const hasChanges = (row, edit) =>
  Boolean(
    edit &&
      (edit.disposition ||
        edit.dealerClosed !== Boolean(row.dealerClosed) ||
        edit.newFiles.length > 0 ||
        edit.filesToDelete.length > 0),
  )

/**
 * Whether a row's own Save commits its edit. Outstanding List is the exception: its form
 * has required fields, so that choice saves through the form, never through the row.
 */
export const savesInPlace = (row, edit) =>
  hasChanges(row, edit) && edit.disposition !== DISPOSITION.OUTSTANDING

/**
 * Whether a file can be removed from the row. The RO tables list line-item files with the
 * RO's own (the API marks them with `lineItem`), but those belong to their line item and
 * are removed on the RO page (WARP-966).
 */
export const isRemovableFile = (file) => !file.lineItem

/**
 * What a user may do from an RO table row (WARP-966, 2026-10-08). Every user sees the
 * same rows. "They could just see what's in our queue. They just can't interact with
 * anything" (Mark), except to add files:
 * - Status and the dealer-closed flag: whoever works the list. That is WPC, and a
 *   self-managed dealer's own admins (Emily, 2026-10-06). WARP-967 reads Michelle as
 *   saying other dealer users must not set dealer closed.
 * - Add files: every user, dealers included.
 * - Remove saved files: only whoever works the list, so a dealer cannot take away a file
 *   WPC relies on. A dealer can still take back a file they added and have not saved.
 * A read-only session (a read-only WPC user, or impersonation) does none of it.
 *
 * The API does not enforce the status rule yet (WARP-964).
 */
export const rowPermissions = ({ canWork, isReadOnlySession }) => {
  const writable = !isReadOnlySession
  return {
    canAttach: writable,
    canChangeStatus: writable && canWork,
    canRemoveFiles: writable && canWork,
  }
}
